import React from "react";

// Series colors validated (dataviz six-checks, light surface): AS-IS blue / TO-BE teal.
export const DQ_ASIS = "#1d4ed8";
export const DQ_TOBE = "#0d9488";

/**
 * `engine.devils_quadrangle` (from GET /cms/comparison-report) into this chart's
 * fixed time/cost/quality/flexibility axis order. Co-located with the chart because
 * it exists only to match this component's contract — every caller that reads a
 * comparison-report engine block for its DQ figure needs this exact shape, so it is
 * defined once here rather than re-derived per caller.
 */
export function axesFromDevilsQuadrangle(dq) {
  if (!dq) return null;
  return [
    { label: "Time", as_is: dq.time?.as_is ?? null, to_be: dq.time?.to_be ?? null, unit: dq.time?.unit || "min", lowerIsBetter: true },
    { label: "Cost", as_is: dq.cost?.as_is ?? null, to_be: dq.cost?.to_be ?? null, unit: dq.cost?.unit || "USD", lowerIsBetter: true },
    { label: "Quality", as_is: dq.quality?.as_is ?? null, to_be: dq.quality?.to_be ?? null, unit: dq.quality?.unit || "/100", lowerIsBetter: false },
    { label: "Flexibility", as_is: dq.flexibility?.as_is ?? null, to_be: dq.flexibility?.to_be ?? null, unit: dq.flexibility?.unit || "/100", lowerIsBetter: false },
  ];
}

/**
 * The FBPM Devil's Quadrangle as a 4-axis figure (the book's own representation).
 * Axes are normalized so OUTWARD = BETTER on every axis: for a lower-is-better
 * dimension (time, cost) the better value gets radius 1 and the other shows the
 * ratio; higher-is-better (quality, flexibility) likewise. The numeric table above
 * the chart remains the accessible/table view; vertex <title>s give native tooltips.
 *
 * `axes` is `[{ label, as_is, to_be, unit, lowerIsBetter }]` in time/cost/quality/
 * flexibility order. A `null` as_is or to_be on any axis draws no vertex for that
 * axis rather than plotting it at the origin — callers must not coerce null to 0
 * before passing it in.
 */
export default function DevilsQuadrangleChart({ axes }) {
  const cx = 195, cy = 128, R = 92;
  const angle = (i) => (Math.PI / 2) * i - Math.PI / 2; // time top, cost right, quality bottom, flex left
  const pt = (i, r) => [cx + Math.cos(angle(i)) * R * r, cy + Math.sin(angle(i)) * R * r];
  const norm = (ax) => {
    const { as_is: a, to_be: b, lowerIsBetter } = ax;
    if (a == null || b == null || (a === 0 && b === 0)) return { ra: 0, rb: 0, na: true };
    if (lowerIsBetter) {
      const best = Math.min(a, b);
      if (best <= 0) return { ra: a <= b ? 1 : 0.4, rb: b <= a ? 1 : 0.4, na: false };
      return { ra: Math.max(0.08, best / a), rb: Math.max(0.08, best / b), na: false };
    }
    const best = Math.max(a, b);
    if (best <= 0) return { ra: 0, rb: 0, na: true };
    return { ra: Math.max(0.08, a / best), rb: Math.max(0.08, b / best), na: false };
  };
  const normed = axes.map(norm);
  const poly = (key) => normed.map((n, i) => pt(i, n[key]).join(",")).join(" ");
  const fmt = (v, ax) => (v == null ? "n/a" : `${Number(v).toLocaleString()} ${ax.unit || ""}`.trim());
  const labelPos = [
    { x: cx, y: cy - R - 14, anchor: "middle" },
    { x: cx + R + 12, y: cy + 4, anchor: "start" },
    { x: cx, y: cy + R + 20, anchor: "middle" },
    { x: cx - R - 12, y: cy + 4, anchor: "end" },
  ];
  return (
    <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
      <svg width="390" height="264" viewBox="0 0 390 264" style={{ maxWidth: "100%" }} role="img" aria-label="Devil's Quadrangle: AS-IS vs TO-BE across time, cost, quality and flexibility (outward is better)">
        {[0.25, 0.5, 0.75, 1].map((g) => (
          <polygon key={g} points={[0, 1, 2, 3].map((i) => pt(i, g).join(",")).join(" ")} fill="none" style={{ stroke: "var(--slate-100)" }} strokeWidth={g === 1 ? 1.5 : 1} />
        ))}
        {[0, 1, 2, 3].map((i) => (
          <line key={i} x1={cx} y1={cy} x2={pt(i, 1)[0]} y2={pt(i, 1)[1]} style={{ stroke: "var(--slate-100)" }} strokeWidth="1" />
        ))}
        <polygon points={poly("ra")} fill={DQ_ASIS} fillOpacity="0.12" stroke={DQ_ASIS} strokeWidth="2" strokeLinejoin="round" />
        <polygon points={poly("rb")} fill={DQ_TOBE} fillOpacity="0.16" stroke={DQ_TOBE} strokeWidth="2" strokeLinejoin="round" />
        {normed.map((n, i) => {
          const [xa, ya] = pt(i, n.ra); const [xb, yb] = pt(i, n.rb);
          return (
            <g key={i}>
              <circle cx={xa} cy={ya} r="4" fill={DQ_ASIS} style={{ stroke: "var(--color-surface)" }} strokeWidth="2"><title>{`${axes[i].label} — AS-IS: ${fmt(axes[i].as_is, axes[i])}`}</title></circle>
              <circle cx={xb} cy={yb} r="4" fill={DQ_TOBE} style={{ stroke: "var(--color-surface)" }} strokeWidth="2"><title>{`${axes[i].label} — TO-BE: ${fmt(axes[i].to_be, axes[i])}`}</title></circle>
            </g>
          );
        })}
        {axes.map((ax, i) => (
          <text key={ax.label} x={labelPos[i].x} y={labelPos[i].y} textAnchor={labelPos[i].anchor} fontSize="11" fontWeight="700" style={{ fill: "var(--gray-500)" }}>
            {ax.label}{normed[i].na ? " (n/a)" : ""}
          </text>
        ))}
      </svg>
      <div style={{ fontSize: 12, color: "var(--gray-700)", minWidth: 170 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: DQ_ASIS, display: "inline-block" }} /> AS-IS (measured)
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: DQ_TOBE, display: "inline-block" }} /> TO-BE (redesign)
        </div>
        <div style={{ fontSize: 11, color: "var(--gray-400)" }}>
          Outward = better on every axis. Time &amp; cost axes are inverted (lower is better);
          identical polygons mean the redesign changes nothing on these dimensions.
        </div>
      </div>
    </div>
  );
}
